/* Research progress (spec 6.8; planner and live trace after GPT Researcher, Apache-2.0):
   phases and counts only, never a fake percentage; "Add direction" while it runs; Stop always visible. */
import { useState, type ReactNode } from 'react';
import { Square } from 'lucide-react';
import { Button } from './basics';

export const RESEARCH_PHASES = ['Plan', 'Search', 'Read', 'Analyze', 'Write'] as const;
export type ResearchPhase = (typeof RESEARCH_PHASES)[number];

export interface ResearchProgressProps {
  phase: ResearchPhase | 'Done';
  consulted: number;
  cited: number;
  estimate?: string;
  feed?: { id: string; icon?: ReactNode; text: ReactNode }[];
  onAddDirection?: (text: string) => void;
  onStop?: () => void;
}

export function ResearchProgress({ phase, consulted, cited, estimate, feed = [], onAddDirection, onStop }: ResearchProgressProps) {
  const [dir, setDir] = useState('');
  const idx = phase === 'Done' ? RESEARCH_PHASES.length : RESEARCH_PHASES.indexOf(phase);
  return (
    <section aria-label="Research progress" data-cap="RESEARCH-01">
      <ol className="phases" aria-label="Phases">
        {RESEARCH_PHASES.map((p, i) => (
          <li key={p} data-state={i < idx ? 'done' : i === idx ? 'now' : 'todo'} aria-current={i === idx ? 'step' : undefined}>{p}</li>
        ))}
      </ol>
      <div className="counters">
        <div><b>{consulted}</b>sources consulted</div>
        <div><b>{cited}</b>cited</div>
        {estimate && <div><b style={{ fontSize: 16 }}>{estimate}</b>estimated time left</div>}
      </div>
      {feed.length > 0 && (
        <ul className="feed" aria-label="Recent activity">
          {feed.map((f) => <li key={f.id}>{f.icon}{f.text}</li>)}
        </ul>
      )}
      <div className="row" style={{ marginTop: 12 }}>
        {onAddDirection && (
          <form className="row" style={{ flex: 1 }} onSubmit={(e) => { e.preventDefault(); if (dir.trim()) { onAddDirection(dir.trim()); setDir(''); } }}>
            <input aria-label="Add a direction" placeholder="Add direction: focus on…" value={dir} onChange={(e) => setDir(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
            <Button type="submit" size="sm">Add</Button>
          </form>
        )}
        {onStop && phase !== 'Done' && <Button size="sm" variant="danger" icon={<Square size={14} aria-hidden="true" />} onClick={onStop}>Stop</Button>}
      </div>
    </section>
  );
}
